import * as React from 'react';
import { cn } from '@relay-towing/tokens';
import {
  chartSwatchRecipe,
  type ChartSwatchRecipeProps,
} from '../recipes/chart-legend.recipe';
import { Inline, type InlineProps } from './Layout';
import { Text } from './Text';

export interface ChartLegendItem {
  label: React.ReactNode;
  /** Which `--chart-N` token the series is drawn with. */
  series: NonNullable<ChartSwatchRecipeProps['series']>;
  /** A filled block for an area, a thin rule for a line. */
  mark?: ChartSwatchRecipeProps['mark'];
}

export interface ChartLegendProps extends Omit<InlineProps, 'children'> {
  items: ChartLegendItem[];
}

/**
 * The key beside a chart.
 *
 *   <ChartLegend items={[
 *     { label: 'Visitors', series: 1 },
 *     { label: 'Views', series: 2, mark: 'line' },
 *   ]} />
 *
 * Static swatches — nothing here moves. Each is painted from the same token
 * the chart draws its series with, so the two cannot drift apart.
 */
export const ChartLegend = React.forwardRef<HTMLDivElement, ChartLegendProps>(
  function ChartLegend({ items, className, ...props }, ref) {
    return (
      <Inline
        ref={ref}
        gap={4}
        align="center"
        wrap
        className={cn(className)}
        {...props}
      >
        {items.map((item, index) => (
          <Inline key={index} gap={2} align="center">
            <span
              aria-hidden="true"
              className={chartSwatchRecipe({
                series: item.series,
                mark: item.mark,
              })}
            />
            <Text variant="caption" tone="muted">
              {item.label}
            </Text>
          </Inline>
        ))}
      </Inline>
    );
  }
);
